
import Grid from "@mui/material/Grid";

// Iris Design Limited  Fulfilment POD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Iris Design Limited  Fulfilment POD example components
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import FulfilOverviewThreeCharts from "components/Charts/FulfilOverviewThreeCharts";
import StoreReportTable from "components/Charts/StoreReportTable";
import FulfilStoreShareCharts from "components/Charts/FulfilStoreShareCharts";

import { Card } from "@mui/material";
import CardContent from "@mui/material/CardContent";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import moment from "moment";
import { useState, useEffect, useCallback, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getDashboard } from "features/slices";
import bcrypt from "bcryptjs";
import { NotificationManager } from "react-notifications";

/** Sắp xếp ngày DD/MM/YYYY — khớp DashboardUpgrade `FulfilDashboardToolbarContext`. */
function sortDateStringsDDMM(dateStrings) {
  return [...dateStrings].sort((a, b) => {
    const parseDate = (dateStr) => {
      const parts = String(dateStr).split("/").map(Number);
      if (parts.length !== 3) return new Date(dateStr).getTime();
      const [day, month, year] = parts;
      return new Date(year, month - 1, day).getTime();
    };
    return parseDate(a) - parseDate(b);
  });
}

/** Gradient nền KPI giống `DashboardUpgrade/public/assets/css/style.css` (.bg-gradient-start-*). */
const KPI_BG = [
  "linear-gradient(to right, #e6f9ff, #feffff)",
  "linear-gradient(to right, #f7e9ff, #fffefd)",
  "linear-gradient(to right, #e6ebff, #ffffff)",
  "linear-gradient(to right, #ffeeee, #fffcfc)",
];

function Dashboard() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);

  const startDate = useSelector((state) => state.user.startDate);
  const endDate = useSelector((state) => state.user.endDate);
  const filterStore = useSelector((state) => state.user.filterStore);
  const order_status = useSelector((state) => state.user.order_status);

  const [dashboard, setDashboard] = useState({
    order: 0,
    price: 0,
    basecost: 0,
    cancel: 0,
    sku: { other: [], total: {} },
    statistics: {},
    storeRevenue: {},
    storeStatistics: {},
  });
  const [loading, setLoading] = useState(false);

  const fetchDashboardData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const salt = await bcrypt.genSalt(10);
      const hash = await bcrypt.hash(`${user}đố tuấn anh phá được`, salt);

      const res = await dispatch(
        getDashboard({
          startDate: moment(startDate).startOf("day").format("x"),
          endDate: moment(endDate).endOf("day").format("x"),
          store: filterStore,
          order_status,
          version: hash,
          type: false,
        })
      ).unwrap();

      if (res) {
        setDashboard(res);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      NotificationManager.error("Server not responding!", "Error", 1000);
      setDashboard({
        price: 0,
        basecost: 0,
        order: 0,
        cancel: 0,
        sku: { other: [], total: {} },
        statistics: {},
        storeRevenue: {},
        storeStatistics: {},
      });
    } finally {
      setLoading(false);
    }
  }, [user, startDate, endDate, filterStore, order_status, dispatch]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatNumber = (num) => {
    if (!num && num !== 0) return "0";
    return Number(num).toLocaleString("en-US");
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "$0.00";
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const cancelRate =
    dashboard?.order && dashboard?.order > 0
      ? ((dashboard.cancel / dashboard.order) * 100).toFixed(2)
      : "0.00";

  const successRate =
    dashboard?.order && dashboard?.order > 0
      ? (((dashboard.order - dashboard.cancel) / dashboard.order) * 100).toFixed(2)
      : "0.00";

  const profit = (
    parseFloat(dashboard?.price || 0) - parseFloat(dashboard?.basecost || 0)
  ).toFixed(2);
  const profitRate =
    dashboard?.price && parseFloat(dashboard.price) > 0
      ? ((parseFloat(profit) / parseFloat(dashboard.price)) * 100).toFixed(2)
      : "0.00";

  const storeCount = dashboard?.storeRevenue ? Object.keys(dashboard.storeRevenue).length : 0;

  const chartPayload = useMemo(() => {
    if (!dashboard?.storeStatistics || Object.keys(dashboard.storeStatistics).length === 0) {
      return { totalOrders: [], totalRevenue: [], totalBasecost: [], hasData: false };
    }
    const allDates = new Set();
    Object.keys(dashboard.storeStatistics).forEach((storeName) => {
      Object.keys(dashboard.storeStatistics[storeName] || {}).forEach((date) => {
        allDates.add(date);
      });
    });
    const sortedDates = sortDateStringsDDMM(Array.from(allDates));

    const totalOrders = sortedDates.map((date) => {
      let total = 0;
      Object.keys(dashboard.storeStatistics).forEach((storeName) => {
        total += dashboard.storeStatistics[storeName][date]?.order || 0;
      });
      return { label: date, value: total };
    });

    const totalRevenue = sortedDates.map((date) => {
      let total = 0;
      Object.keys(dashboard.storeStatistics).forEach((storeName) => {
        const price = Number(dashboard.storeStatistics[storeName][date]?.totalPrice) || 0;
        total += price;
      });
      return { label: date, value: Math.round(total) };
    });

    const totalBasecost = sortedDates.map((date) => {
      let total = 0;
      Object.keys(dashboard.storeStatistics).forEach((storeName) => {
        total += dashboard.storeStatistics[storeName][date]?.basecost || 0;
      });
      return { label: date, value: Math.round(total) };
    });

    return { totalOrders, totalRevenue, totalBasecost, hasData: true };
  }, [dashboard?.storeStatistics]);

  const orders = dashboard?.order || 0;
  const revenue = dashboard?.price || 0;
  const basecostTotal = dashboard?.basecost || 0;
  const fulfilled = (orders || 0) - (dashboard?.cancel || 0);
  const cancelled = dashboard?.cancel || 0;

  const basecostPct =
    revenue && parseFloat(revenue) > 0
      ? ((parseFloat(basecostTotal) / parseFloat(revenue)) * 100).toFixed(1)
      : "0";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
            <MDTypography variant="body2" color="text">
              Đang tải dữ liệu...
            </MDTypography>
          </MDBox>
        ) : null}

        {/* Hàng KPI — bố cục giống `FulfilDashboardLayer` (DashboardUpgrade) */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                border: "1px solid #e9ecef",
                borderRadius: 2,
                boxShadow: "none",
                background: KPI_BG[0],
                height: "100%",
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <MDBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <MDBox sx={{ minWidth: 0, flexGrow: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", display: "block", mb: 0.5 }}
                    >
                      Orders
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ mb: 0, fontWeight: 600, fontSize: "1.1rem" }}>
                      {formatNumber(orders)}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor: "#06b6d4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ShoppingCartIcon sx={{ color: "#fff", fontSize: 22 }} />
                  </MDBox>
                </MDBox>
                <MDTypography
                  variant="caption"
                  sx={{
                    mt: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "#6c757d",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  <TrendingUpIcon sx={{ fontSize: 14 }} />
                  {storeCount} stores
                </MDTypography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                border: "1px solid #e9ecef",
                borderRadius: 2,
                boxShadow: "none",
                background: KPI_BG[1],
                height: "100%",
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <MDBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <MDBox sx={{ minWidth: 0, flexGrow: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", display: "block", mb: 0.5 }}
                    >
                      Revenue
                    </MDTypography>
                    <MDTypography
                      variant="h6"
                      sx={{ mb: 0, fontWeight: 600, fontSize: "0.95rem" }}
                      title={formatCurrency(revenue)}
                    >
                      {formatCurrency(revenue)}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor: "#9333ea",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <AttachMoneyIcon sx={{ color: "#fff", fontSize: 22 }} />
                  </MDBox>
                </MDBox>
                <MDTypography
                  variant="caption"
                  sx={{
                    mt: 1.25,
                    display: "block",
                    color: "#6c757d",
                    fontWeight: 600,
                    fontSize: "11px",
                  }}
                >
                  Profit {formatCurrency(profit)} ({profitRate}%)
                </MDTypography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                border: "1px solid #e9ecef",
                borderRadius: 2,
                boxShadow: "none",
                background: KPI_BG[2],
                height: "100%",
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <MDBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <MDBox sx={{ minWidth: 0, flexGrow: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", display: "block", mb: 0.5 }}
                    >
                      Basecost
                    </MDTypography>
                    <MDTypography variant="h6" sx={{ mb: 0, fontWeight: 600, fontSize: "0.95rem" }}>
                      {formatCurrency(basecostTotal)}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor: "#0284c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <InventoryIcon sx={{ color: "#fff", fontSize: 22 }} />
                  </MDBox>
                </MDBox>
                <MDTypography variant="caption" sx={{ mt: 1.25, display: "block", color: "#6c757d", fontSize: "11px" }}>
                  {basecostPct}% of revenue
                </MDTypography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Card
              sx={{
                border: "1px solid #e9ecef",
                borderRadius: 2,
                boxShadow: "none",
                background: KPI_BG[3],
                height: "100%",
              }}
            >
              <CardContent sx={{ py: 2, px: 2 }}>
                <MDBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                  <MDBox sx={{ minWidth: 0, flexGrow: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", display: "block", mb: 0.5 }}
                    >
                      Fulfilment
                    </MDTypography>
                    <MDBox sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
                      <MDTypography variant="h6" sx={{ mb: 0, fontWeight: 600, fontSize: "1rem" }}>
                        {formatNumber(fulfilled)}
                      </MDTypography>
                      <MDTypography variant="caption" color="text.secondary">
                        /
                      </MDTypography>
                      <MDTypography variant="h6" sx={{ mb: 0, fontWeight: 600, fontSize: "1rem" }}>
                        {formatNumber(cancelled)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      bgcolor: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ShowChartIcon sx={{ color: "#fff", fontSize: 22 }} />
                  </MDBox>
                </MDBox>
                <MDTypography
                  variant="caption"
                  sx={{
                    mt: 1.25,
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: 0.5,
                    color: "#6c757d",
                    fontSize: "11px",
                  }}
                >
                  <span>Hoàn thành {successRate}%</span>
                  <span>Hủy {cancelRate}%</span>
                </MDTypography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Chart chính + cột Tổng quan — giống WowDash */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} xl={9}>
            <Card sx={{ height: "100%", border: "1px solid #e9ecef", boxShadow: "none", borderRadius: 2 }}>
              <CardContent>
                <MDTypography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mb: 2 }}>
                  Dashboard Overview
                </MDTypography>
                {!chartPayload.hasData ? (
                  <MDBox
                    sx={{
                      minHeight: 280,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "text.secondary",
                    }}
                  >
                    Không có dữ liệu chart
                  </MDBox>
                ) : (
                  <FulfilOverviewThreeCharts
                    ordersData={chartPayload.totalOrders}
                    revenueData={chartPayload.totalRevenue}
                    basecostData={chartPayload.totalBasecost}
                    height={320}
                  />
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} xl={3}>
            <Card sx={{ height: "100%", border: "1px solid #e9ecef", boxShadow: "none", borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <MDTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Tổng quan
                </MDTypography>
                <MDBox sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
                  <MDTypography variant="h6" sx={{ fontWeight: 600, flex: "1 1 auto", minWidth: 0 }} noWrap>
                    {formatCurrency(profit)}
                  </MDTypography>
                  <MDBox
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "rgba(34, 197, 94, 0.12)",
                      border: "1px solid rgba(34, 197, 94, 0.35)",
                      borderRadius: 999,
                      px: 1,
                      py: 0.25,
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#16a34a",
                    }}
                  >
                    {profitRate}%
                    <NorthEastIcon sx={{ fontSize: 12 }} />
                  </MDBox>
                </MDBox>
                <MDBox component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {[
                    ["Revenue", formatCurrency(revenue)],
                    ["Basecost", formatCurrency(basecostTotal)],
                    ["Orders", formatNumber(orders)],
                    ["Tỷ lệ hoàn thành", `${successRate}%`],
                  ].map(([label, val]) => (
                    <MDBox
                      key={label}
                      component="li"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                        py: 1.25,
                        borderBottom: "1px solid #eee",
                        "&:last-of-type": { borderBottom: "none" },
                      }}
                    >
                      <MDTypography variant="caption" color="text.secondary">
                        {label}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ fontWeight: 600, textAlign: "right", maxWidth: "58%" }} noWrap>
                        {val}
                      </MDTypography>
                    </MDBox>
                  ))}
                </MDBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Bảng store + biểu đồ tỉ lệ — giống `FulfilStoreReportTable` + `FulfilStoreShareCharts` */}
        <Grid container spacing={3}>
          <Grid item xs={12} xl={8}>
            <StoreReportTable storeStatistics={dashboard?.storeStatistics || {}} />
          </Grid>
          <Grid item xs={12} xl={4}>
            <FulfilStoreShareCharts storeStatistics={dashboard?.storeStatistics || {}} />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
